
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AvatarUpload } from "@/components/AvatarUpload";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/components/AuthProvider";
import { getRandomAvatar, updateProfileAvatar } from "@/lib/avatarUtils";

interface ProfileCreationProps {
  isOpen: boolean;
  onClose: () => void;
  profileId?: string;
  initialName?: string;
  initialAvatarUrl?: string | null;
  initialProfileType?: string;
  isNewAccount?: boolean;
  onSave: (profileId: string, name: string, avatarUrl: string | null, profileType?: string) => Promise<void> | void;
}

export function ProfileCreation({
  isOpen,
  onClose,
  profileId,
  initialName = "",
  initialAvatarUrl = null,
  initialProfileType = "standard",
  isNewAccount = false,
  onSave
}: ProfileCreationProps) {
  const { session } = useAuth();
  const { toast } = useToast();
  const [name, setName] = useState(initialName);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(initialAvatarUrl);
  const [profileType, setProfileType] = useState(initialProfileType);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigningAvatar, setIsAssigningAvatar] = useState(false);
  const isNewProfile = !profileId;

  useEffect(() => {
    setName(initialName);
    // Handle undefined/null initialProfileType - default to 'standard'
    setProfileType(initialProfileType || 'standard');
    
    if (isOpen && (!initialAvatarUrl || initialAvatarUrl === null) && isNewProfile) {
      assignRandomAvatar();
    } else {
      setAvatarUrl(initialAvatarUrl);
    }
  }, [initialName, initialAvatarUrl, initialProfileType, isOpen, isNewProfile]);

  const assignRandomAvatar = async () => {
    setIsAssigningAvatar(true);
    try {
      const randomAvatar = await getRandomAvatar();
      if (randomAvatar) {
        setAvatarUrl(randomAvatar);
      }
    } catch (error) {
      console.error("Error assigning random avatar:", error);
    } finally {
      setIsAssigningAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Profile name cannot be empty",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const id = profileId || "new";
      const finalAvatarUrl = avatarUrl || await getRandomAvatar();
      
      // Await the save operation to ensure it completes before closing
      await onSave(id, name, finalAvatarUrl, profileType);
      
      onClose();
      
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: "Error",
        description: "Failed to save profile",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarUpload = async (url: string) => {
    setAvatarUrl(url);

    // Persist immediately when editing an existing profile so header updates right away
    if (profileId && session?.user?.id) {
      const result = await updateProfileAvatar(profileId, url, session.user.id);
      
      if (result.success) {
        toast({ title: 'Avatar updated', description: 'Profile picture saved.' });
      } else {
        toast({ title: 'Error', description: 'Failed to save avatar', variant: 'destructive' });
      }
    }
  };
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-gray-900/95 border border-gray-700/50 text-white backdrop-blur-sm">
        <DialogHeader>
          <DialogTitle className="text-xl font-light text-white text-center">
            {isNewProfile 
              ? isNewAccount 
                ? "Create Your Profile" 
                : "Add New Profile" 
              : "Edit Profile"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col space-y-6 py-4">
          <div className="flex justify-center">
            {isAssigningAvatar ? (
              <div className="h-20 w-20 rounded-full bg-gray-800 border-2 border-gray-600 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-500"></div>
              </div>
            ) : (
              <AvatarUpload 
                userId={session?.user?.id || "temp"}
                url={avatarUrl}
                onUploadComplete={handleAvatarUpload}
                size="lg"
              />
            )}
          </div>
          
          <div className="space-y-2">
            <label htmlFor="profileName" className="text-sm font-light text-gray-300">
              Profile Name
            </label>
            <Input
              id="profileName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter profile name"
              className="bg-gray-800/50 border-gray-600/50 text-white placeholder:text-gray-400 focus:border-gray-500 font-light"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="profileType" className="text-sm font-light text-gray-300">
              Profile Type
            </label>
            <Select value={profileType} onValueChange={setProfileType}>
              <SelectTrigger className="bg-gray-800/50 border-gray-600/50 text-white focus:border-gray-500">
                <SelectValue placeholder="Select profile type" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                <SelectItem value="standard" className="text-white">Standard</SelectItem>
                <SelectItem value="kids" className="text-white">Kids</SelectItem>
              </SelectContent>
            </Select>
            {profileType === 'kids' && (
              <p className="text-xs text-blue-400">
                Kids profiles have content restrictions and simplified interface.
              </p>
            )}
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="border-gray-600/50 text-gray-300 hover:bg-gray-800/30 bg-transparent font-light"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              className="bg-white text-black hover:bg-gray-200 font-light"
              disabled={isLoading || isAssigningAvatar}
            >
              {isLoading ? "Saving..." : isNewProfile ? "Create Profile" : "Save Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
